/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS — the one port of the reference app's DataTable + Panel
   (frontend/src/components/DataTable.tsx, panels.tsx), used by every GrowthOS page:
     • "#" S.No first column (page-aware; serial={false} hides it)
     • sticky header, the table scrolls INSIDE its card on both axes
     • pagination 25/50/100 with prev/next (paginate={false} renders every row)
     • optional column sort (click header; desc first, nulls last) and filter box
     • optional grouped header row, tinted column groups, row click / active row,
       master-detail expanded row, pinned footer row
   Column: { key, header, title?, align?, cell(row, absIndex), sortValue?(row),
             cellSx?, headSx? (alias headerSx), sx? (head + body), group?, edge? }
     group: 'ship' | 'a' (indigo box) or 'rev' | 'b' (cyan box); edge: 'left' | 'right' | 'both'
   HeaderGroup: { label, span, title?, align?, sx?, group? | variant? } — spans count data columns only.
   `variant` picks the look each page was built with (see VARIANTS); behaviour is identical.
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useMemo, useState } from 'react'
import {
  Box, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography, InputBase,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { GX_BORDER, GX_CARD_SX, GX_COLORS, GX_INK_500, GX_INK_700, GX_INK_900 } from './GrowthOsUi'
import { GrowthPct, currency, integer } from './gxFormat'

const PAGE_SIZES = [25, 50, 100]
const INK_400 = '#94a3b8'
const HEAD_BG = '#f8fafc'
const LINE = `1px solid ${GX_BORDER}`

const headSx = (px, py, letterSpacing, extra) => ({
  px, py, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing,
  color: GX_INK_500, borderBottom: LINE, whiteSpace: 'nowrap', ...extra,
})
const bodySx = (px, py, fontSize) => ({ px, py, fontSize, color: GX_INK_700, borderBottom: LINE, whiteSpace: 'nowrap' })

export const gxHeadCellSx = headSx(1.5, 1.25, '0.08em', { bgcolor: HEAD_BG })
export const gxBodyCellSx = bodySx(1.5, 1, 12)
export const gxInt = integer

// Boxed column groups (.tbl-group-* in the reference): indigo = shipments, cyan = revenue.
const TONES = {
  ship: { head: 'rgba(79,70,229,0.07)', cell: 'rgba(79,70,229,0.045)', line: 'rgba(79,70,229,0.35)' },
  rev: { head: 'rgba(6,182,212,0.08)', cell: 'rgba(6,182,212,0.05)', line: 'rgba(6,182,212,0.4)' },
}
const TONE_KEY = { ship: 'ship', a: 'ship', rev: 'rev', b: 'rev' }
const columnToneSx = (group, edge) => {
  if (!group) return null
  const t = TONES[TONE_KEY[group] || 'ship']
  return {
    bgcolor: t.cell,
    ...(edge === 'left' || edge === 'both' ? { borderLeft: `1px solid ${t.line}` } : {}),
    ...(edge === 'right' || edge === 'both' ? { borderRight: `1px solid ${t.line}` } : {}),
  }
}

const GRADIENT_HOVER = { background: 'linear-gradient(90deg, rgba(79,70,229,0.06), rgba(6,182,212,0.04))', boxShadow: 'inset 3px 0 0 #4f46e5' }
const NATIVE_PAGER_BTN = {
  display: 'inline-flex', alignItems: 'center', border: LINE, borderRadius: '6px', p: 0.25,
  bgcolor: 'transparent', cursor: 'pointer', color: GX_INK_500, '&:disabled': { opacity: 0.4, cursor: 'default' },
}

/**
 * The look each GrowthOS page was built with. Keys:
 *   mui        render with MUI Table* components (else plain table elements)
 *   th/td      base head / body cell sx; dense = body override when dense; denseEmpty (default true)
 *   tableSx, theadSx, containerSx, capUnpaged (keep max-height when paginate={false})
 *   groupTh    extra sx for grouped-header-row cells; groupedTop = column-header `top` under a group row
 *   toneGroupColor / toneHeadBg  tinted group header text colour / opaque head fill per tone
 *   serialColor, sortIcon ('expand' | 'arrow'), sortGap, sortHoverColor, headerTooltip
 *   selectableHeads (user-select only locked on sortable heads), headCursor: 'sortable' (cursor only on sortable heads)
 *   row(active, zebra, clickable) → row sx; rowCursor (default true); muiHover
 *   expanded(td) → expanded-row cell sx; emptySx; filter; pager
 */
const VARIANTS = {
  // Email Marketing, LinkedIn ABM
  default: {
    mui: true, th: gxHeadCellSx, td: gxBodyCellSx, serialColor: GX_INK_500, sortIcon: 'expand', sortGap: 0.25, muiHover: true,
    row: (active, zebra) => ({ bgcolor: active ? `${GX_COLORS.indigo}10` : (zebra ? '#fbfcfe' : undefined) }),
    emptySx: { color: GX_INK_500 },
    filter: { width: 260 },
    pager: { text: 'typo', selectSx: { fontSize: 12, height: 28 }, button: 'icon' },
  },
  // Sales POC analytics, Data → Sales POCs, Connectors
  poc: {
    th: headSx(1.5, 1.1, '0.08em', { position: 'sticky', top: 0, zIndex: 1, bgcolor: HEAD_BG, textAlign: 'left' }),
    td: { ...bodySx(1.5, 1, 12.5), verticalAlign: 'middle' },
    groupedTop: 33, containerSx: { bgcolor: '#fff' }, capUnpaged: true,
    serialColor: INK_400, sortIcon: 'arrow', sortGap: 0.25,
    row: (active, zebra, clickable) => ({
      bgcolor: active ? '#eef0fd' : (zebra ? '#fafbfd' : 'transparent'),
      '&:hover': clickable ? { bgcolor: active ? '#eef0fd' : '#f3f5fb' } : undefined,
    }),
    emptySx: { color: INK_400, py: 2 },
    filter: { py: 0.4, iconColor: INK_400, inputColor: null },
    pager: { text: 'typo', tabularRange: true, selectSx: { fontSize: 12, '& .MuiSelect-select': { py: 0.4, pl: 1 } }, button: 'icon', rightGap: 1 },
  },
  // Executive
  executive: {
    tableSx: { fontSize: 14 }, theadSx: { position: 'sticky', top: 0, zIndex: 10, bgcolor: HEAD_BG }, containerSx: { bgcolor: '#fff' },
    th: headSx(2, 1.5, '0.1em', { textAlign: 'left' }), td: bodySx(2, 1.5),
    serialColor: INK_400, sortIcon: 'expand', sortGap: 0.5, selectableHeads: true,
    row: (active, zebra) => ({
      transition: 'background-color 180ms ease, box-shadow 180ms ease',
      ...(zebra && !active ? { backgroundColor: 'rgba(15,23,42,0.035)' } : {}),
      '&:hover': GRADIENT_HOVER,
      ...(active ? { background: '#eef0fd !important', boxShadow: 'inset 3px 0 0 #4f46e5' } : {}),
    }),
    expanded: (td) => ({ ...td, p: 0 }),
    emptySx: { color: INK_400 },
    pager: {
      text: 'span', tabularRange: true, tabularPage: true, grouped: true, button: 'native', iconSize: 14, btnSx: NATIVE_PAGER_BTN,
      select: 'native', selectSx: { borderRadius: '6px', border: LINE, bgcolor: 'transparent', px: 0.75, py: 0.5, fontSize: 12, color: GX_INK_700 },
    },
  },
  // Marketing / Funnel
  marketing: {
    mui: true, th: headSx(1.25, 1, '0.06em', { bgcolor: HEAD_BG }), td: bodySx(1.25, 1, 12.5), dense: { py: 0.75 },
    groupTh: { top: 0, height: 30, py: 0.5 }, groupedTop: 30, toneGroupColor: GX_INK_700, containerSx: { bgcolor: '#fff' },
    serialColor: INK_400, sortIcon: 'arrow', sortGap: 0, muiHover: true,
    row: (active, zebra) => ({ bgcolor: active ? '#eef0fd' : (zebra ? '#fafbfd' : 'transparent') }),
    emptySx: { color: INK_400 },
    filter: { inputColor: null },
    pager: { text: 'typo', tabularRange: true, tabularPage: true, selectSx: { fontSize: 12, height: 26 }, button: 'iconBordered' },
  },
  // SME Performance, Retention (KAM)
  sme: {
    theadSx: { position: 'sticky', top: 0, zIndex: 2 },
    th: headSx(2, 1.5, '0.1em', { textAlign: 'left', bgcolor: HEAD_BG }), td: bodySx(2, 1.5, 13),
    toneHeadBg: { ship: '#f4f4fd', rev: '#f1fbfd' }, sortHoverColor: '#4f46e5', selectableHeads: true, headCursor: 'sortable',
    serialColor: INK_400, sortIcon: 'expand', sortGap: 0.25, rowCursor: false,
    row: (active, zebra) => ({ bgcolor: zebra ? 'rgba(15,23,42,0.035)' : 'transparent', '&:hover': GRADIENT_HOVER }),
    emptySx: { color: INK_400 },
    filter: { iconColor: INK_400, inputColor: null },
    pager: {
      text: 'typoSpan', tabularRange: true, tabularPage: true, grouped: true, button: 'native', iconSize: 16, btnSx: NATIVE_PAGER_BTN,
      selectSx: { fontSize: 12, '& .MuiSelect-select': { py: 0.5, px: 1 } },
    },
  },
  // HubSpot Review, Decision / Insights
  hubspot: {
    th: headSx(1.5, 1.1, '0.06em', { position: 'sticky', top: 0, zIndex: 1, bgcolor: HEAD_BG }), groupTh: { position: 'static' },
    td: bodySx(1.5, 1, 12.5), dense: { py: 0.6, px: 1.25 }, containerSx: { bgcolor: '#fff' },
    serialColor: INK_400, sortIcon: 'expand', sortGap: 0.25,
    row: (active, zebra, clickable) => ({
      bgcolor: active ? '#eef0fd' : (zebra ? '#fafbfe' : '#fff'),
      '&:hover': clickable ? { bgcolor: '#f3f4fd' } : undefined,
    }),
    emptySx: { color: INK_400 },
    filter: { py: 0.75, iconSize: 14, iconColor: INK_400, native: true },
    pager: {
      text: 'span', grouped: true, button: 'native', iconSize: 16,
      btnSx: { display: 'inline-flex', border: LINE, borderRadius: '6px', p: 0.25, bgcolor: 'transparent', cursor: 'pointer', '&:disabled': { opacity: 0.4, cursor: 'default' } },
      select: 'native', selectSx: { border: LINE, borderRadius: '6px', px: 0.5, py: 0.25, fontSize: 12, bgcolor: 'transparent' },
    },
  },
  // Pipeline Funnel
  pipeline: {
    mui: true, th: headSx(1.5, 1.25, '0.06em', { bgcolor: HEAD_BG }), td: bodySx(1.5, 1, 12.5), dense: { px: 1.25, py: 0.75 }, denseEmpty: false,
    containerSx: { bgcolor: '#fff' }, headerTooltip: true,
    serialColor: INK_400, sortIcon: 'arrow', sortGap: 0, muiHover: true,
    row: (active, zebra) => ({ bgcolor: active ? '#eef0fd' : (zebra ? '#fafbfd' : undefined) }),
    expanded: () => ({ p: 0, borderBottom: LINE, bgcolor: '#fbfbfe' }),
    emptySx: { color: INK_400 },
    filter: { iconColor: INK_400, inputColor: null, ariaLabel: true },
    pager: { text: 'span', grouped: true, button: 'iconBordered', selectSx: { fontSize: 12, '& .MuiSelect-select': { py: 0.25, pl: 1 } } },
  },
}

const DEFAULT_EXPANDED = () => ({ p: 0, borderBottom: LINE })

// Element sets: MUI Table* components, or plain table elements styled through Box.
const MuiTable = (props) => <Table size="small" stickyHeader {...props} />
const MUI_EL = { Container: TableContainer, Table: MuiTable, Head: TableHead, Body: TableBody, Row: TableRow, Th: TableCell, Td: TableCell }
const RAW_EL = {
  Container: Box,
  Table: ({ sx, ...p }) => <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, ...sx }} {...p} />,
  Head: (p) => <Box component="thead" {...p} />,
  Body: (p) => <tbody {...p} />,
  Row: (p) => <Box component="tr" {...p} />,
  Th: (p) => <Box component="th" {...p} />,
  Td: (p) => <Box component="td" {...p} />,
}

export function GxFilterInput({
  value, onChange, placeholder, width = 260, py = 0.5, iconSize = 15, iconColor = GX_INK_500, inputColor = GX_INK_900,
  native = false, ariaLabel = false,
}) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, border: LINE, borderRadius: '8px',
      px: 1.25, py, bgcolor: '#fbfcfe', width, maxWidth: width ? '100%' : 320,
    }}>
      <SearchIcon sx={{ fontSize: iconSize, color: iconColor }} />
      {native
        ? <Box component="input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            sx={{ width: '100%', border: 'none', outline: 'none', bgcolor: 'transparent', fontSize: 12, color: GX_INK_900, fontFamily: 'inherit' }} />
        : <InputBase value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            inputProps={ariaLabel ? { 'aria-label': placeholder } : undefined}
            sx={{ fontSize: 12, flex: 1, color: inputColor }} />}
    </Box>
  )
}

// Pager text: 'typo' = Typography paragraph, 'typoSpan' = Typography span, 'span' = plain inherited-font span.
function PagerText({ kind, tabular, children }) {
  const num = tabular ? { fontVariantNumeric: 'tabular-nums' } : {}
  if (kind === 'typo') return <Typography sx={{ fontSize: 12, color: GX_INK_500, ...num }}>{children}</Typography>
  if (kind === 'typoSpan') return <Typography component="span" sx={{ fontSize: 12, ...num }}>{children}</Typography>
  return <Box component="span" sx={tabular ? num : undefined}>{children}</Box>
}

function Pager({ p, start, pageSize, total, safePage, pageCount, setPage, setPageSize }) {
  const onSize = (e) => { setPageSize(Number(e.target.value)); setPage(0) }
  const nav = (dir) => {
    const Icon = dir < 0 ? ChevronLeftIcon : ChevronRightIcon
    const props = {
      'aria-label': dir < 0 ? 'Previous page' : 'Next page',
      disabled: dir < 0 ? safePage === 0 : safePage >= pageCount - 1,
      onClick: () => setPage((pg) => (dir < 0 ? Math.max(0, pg - 1) : Math.min(pageCount - 1, pg + 1))),
    }
    if (p.button === 'native') return <Box component="button" type="button" sx={p.btnSx} {...props}><Icon sx={{ fontSize: p.iconSize }} /></Box>
    if (p.button === 'iconBordered') return <IconButton size="small" sx={{ border: LINE, borderRadius: '6px', p: 0.25 }} {...props}><Icon sx={{ fontSize: 16 }} /></IconButton>
    return <IconButton size="small" {...props}><Icon fontSize="small" /></IconButton>
  }
  const pageNav = [nav(-1), <PagerText kind={p.text} key="pg" tabular={p.tabularPage}>Page {safePage + 1} / {pageCount}</PagerText>, nav(1)]
  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, fontSize: 12, color: GX_INK_500 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PagerText kind={p.text}>Rows per page</PagerText>
        {p.select === 'native'
          ? <Box component="select" value={pageSize} onChange={onSize} sx={p.selectSx}>{PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}</Box>
          : <Select size="small" value={pageSize} onChange={onSize} sx={p.selectSx}>{PAGE_SIZES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>)}</Select>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: p.rightGap || 1.5 }}>
        <PagerText kind={p.text} tabular={p.tabularRange}>
          {total === 0 ? '0' : `${integer(start + 1)}–${integer(Math.min(start + pageSize, total))}`} of {integer(total)}
        </PagerText>
        {p.grouped
          ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{React.Children.toArray(pageNav)}</Box>
          : React.Children.toArray(pageNav)}
      </Box>
    </Box>
  )
}

export function GxPagedTable({
  rows, columns, rowKey, initialPageSize = 25, maxBodyHeight = 560,
  filter, filterPlaceholder = 'Filter…', emptyText = 'No rows.', dense = false,
  onRowClick, isRowActive, renderExpanded, headerGroups, footer, paginate = true, serial = true,
  variant = 'default',
}) {
  const v = VARIANTS[variant] || VARIANTS.default
  const El = v.mui ? MUI_EL : RAW_EL
  const [q, setQ] = useState('')
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [page, setPage] = useState(0)
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('desc')

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    return filter && t ? (rows || []).filter((r) => filter(r, t)) : (rows || [])
  }, [rows, q, filter])

  const sorted = useMemo(() => {
    const col = sortKey ? columns.find((c) => c.key === sortKey) : null
    if (!col || !col.sortValue) return filtered
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = col.sortValue(a); const bv = col.sortValue(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1 // nulls last regardless of direction
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [filtered, columns, sortKey, sortDir])

  const total = sorted.length
  const pageCount = paginate ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const safePage = paginate ? Math.min(page, pageCount - 1) : 0
  const start = paginate ? safePage * pageSize : 0
  const pageRows = paginate ? sorted.slice(start, start + pageSize) : sorted
  const colCount = columns.length + (serial ? 1 : 0)

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else { setSortKey(key); setSortDir('desc') }
    setPage(0)
  }

  const th = v.th
  const td = dense && v.dense ? { ...v.td, ...v.dense } : v.td
  const headTop = headerGroups && v.groupedTop ? { top: v.groupedTop } : null
  const groupTh = { ...th, ...(v.groupTh || {}) }
  const SortUp = v.sortIcon === 'arrow' ? ArrowDropUpIcon : ExpandLessIcon
  const SortDown = v.sortIcon === 'arrow' ? ArrowDropDownIcon : ExpandMoreIcon
  const sortIconSx = { fontSize: v.sortIcon === 'arrow' ? 16 : 14 }

  const groupCellSx = (g) => {
    const t = TONES[TONE_KEY[g.variant || g.group]]
    return {
      ...groupTh, textAlign: g.align || 'center',
      ...(t ? { bgcolor: t.head, borderLeft: `1px solid ${t.line}`, borderRight: `1px solid ${t.line}`, borderTop: `1px solid ${t.line}`, ...(v.toneGroupColor ? { color: v.toneGroupColor } : {}) } : {}),
      ...(g.sx || {}),
    }
  }
  const colHeadSx = (c) => ({
    ...th, ...headTop, textAlign: c.align || 'left',
    ...(v.headCursor === 'sortable' && !c.sortValue ? {} : { cursor: c.sortValue ? 'pointer' : 'default' }),
    ...(!v.selectableHeads || c.sortValue ? { userSelect: 'none' } : {}),
    ...columnToneSx(c.group, c.edge),
    ...(c.group && v.toneHeadBg ? { bgcolor: v.toneHeadBg[TONE_KEY[c.group] || 'ship'] } : {}),
    ...(c.sortValue && v.sortHoverColor ? { '&:hover': { color: v.sortHoverColor } } : {}),
    ...(c.headSx || c.headerSx || {}), ...(c.sx || {}),
  })
  const headInner = (c) => {
    const inner = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ...(v.sortGap ? { gap: v.sortGap } : {}) }}>
        {c.header}
        {c.sortValue && sortKey === c.key && (sortDir === 'desc' ? <SortDown sx={sortIconSx} /> : <SortUp sx={sortIconSx} />)}
      </Box>
    )
    return v.headerTooltip ? <Tooltip title={c.title || ''} arrow disableHoverListener={!c.title}>{inner}</Tooltip> : inner
  }
  const maxHeight = v.capUnpaged ? (maxBodyHeight || undefined) : (paginate ? maxBodyHeight : undefined)

  return (
    <Box>
      {filter && (
        <Box sx={{ mb: 1 }}>
          <GxFilterInput value={q} onChange={(val) => { setQ(val); setPage(0) }} placeholder={filterPlaceholder} {...(v.filter || {})} width={v.filter?.width ?? null} />
        </Box>
      )}
      <El.Container sx={{ border: LINE, borderRadius: '12px', maxHeight, overflow: 'auto', ...(v.containerSx || {}) }}>
        <El.Table sx={v.tableSx}>
          <El.Head sx={v.theadSx}>
            {headerGroups && (
              <El.Row>
                {serial && <El.Th aria-hidden sx={{ ...groupTh, width: 44 }} />}
                {headerGroups.map((g, gi) => <El.Th key={gi} colSpan={g.span} title={g.title} sx={groupCellSx(g)}>{g.label}</El.Th>)}
              </El.Row>
            )}
            <El.Row>
              {serial && <El.Th title="Serial number (continues across pages)." sx={{ ...th, ...headTop, width: 44, textAlign: 'right' }}>#</El.Th>}
              {columns.map((c) => (
                <El.Th key={c.key} title={v.headerTooltip ? undefined : c.title} onClick={c.sortValue ? () => toggleSort(c.key) : undefined} sx={colHeadSx(c)}>
                  {headInner(c)}
                </El.Th>
              ))}
            </El.Row>
          </El.Head>
          <El.Body>
            {pageRows.map((row, i) => {
              const abs = start + i
              const active = isRowActive ? isRowActive(row) : false
              const expanded = active && renderExpanded ? renderExpanded(row, abs) : null
              const clickable = Boolean(onRowClick)
              return (
                <React.Fragment key={rowKey(row, abs)}>
                  <El.Row
                    {...(v.mui ? { hover: v.muiHover && clickable } : {})}
                    onClick={clickable ? () => onRowClick(row, abs) : undefined}
                    sx={{ ...(v.rowCursor === false ? {} : { cursor: clickable ? 'pointer' : 'default' }), ...v.row(active, i % 2 === 1, clickable) }}
                  >
                    {serial && <El.Td sx={{ ...td, textAlign: 'right', color: v.serialColor, fontVariantNumeric: 'tabular-nums' }}>{abs + 1}</El.Td>}
                    {columns.map((c) => (
                      <El.Td key={c.key} sx={{ ...td, textAlign: c.align || 'left', ...columnToneSx(c.group, c.edge), ...(c.sx || {}), ...(c.cellSx || {}) }}>
                        {c.cell(row, abs)}
                      </El.Td>
                    ))}
                  </El.Row>
                  {expanded != null && (
                    <El.Row><El.Td colSpan={colCount} sx={(v.expanded || DEFAULT_EXPANDED)(td)}>{expanded}</El.Td></El.Row>
                  )}
                </React.Fragment>
              )
            })}
            {Array.isArray(footer) ? (total > 0 && (
              <El.Row sx={{ bgcolor: HEAD_BG }}>
                {serial && <El.Td sx={{ ...td, fontWeight: 700 }} />}
                {footer.map((f, fi) => (
                  <El.Td key={fi} sx={{ ...td, textAlign: columns[fi]?.align || 'left', fontWeight: 700, color: GX_INK_900 }}>{f}</El.Td>
                ))}
              </El.Row>
            )) : footer}
            {total === 0 && (
              <El.Row>
                <El.Td colSpan={colCount} sx={{ ...(v.denseEmpty === false ? v.td : td), textAlign: 'center', ...v.emptySx }}>{emptyText}</El.Td>
              </El.Row>
            )}
          </El.Body>
        </El.Table>
      </El.Container>
      {paginate && (
        <Pager p={v.pager} start={start} pageSize={pageSize} total={total} safePage={safePage} pageCount={pageCount}
          setPage={setPage} setPageSize={setPageSize} />
      )}
    </Box>
  )
}

/** GxPagedTable that owns its own single-row expansion (click a row to open its detail). */
export function GxExpandableTable({ rowKey, renderExpanded, ...rest }) {
  const [openKey, setOpenKey] = useState(null)
  return (
    <GxPagedTable
      {...rest}
      rowKey={rowKey}
      onRowClick={(row) => setOpenKey((k) => (k === rowKey(row) ? null : rowKey(row)))}
      isRowActive={(row) => rowKey(row) === openKey}
      renderExpanded={renderExpanded}
    />
  )
}

// ── Grouped "Comparable · Current · Δ%" metric columns (Executive, SME) ──────
export const GROUPED_METRIC_GROUPS = [
  { label: 'Shipments', span: 3, group: 'ship', title: 'ProShip shipment counts — comparable prior window vs the selected window.' },
  { label: 'Revenue', span: 3, group: 'rev', title: 'ProShip revenue (₹) — comparable prior window vs the selected window.' },
]
export function periodLeadGroup(span, timeframe) {
  return {
    span,
    align: 'left',
    label: timeframe ? (
      <Box component="span" sx={{ display: 'block', lineHeight: 1.35, textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>
        <Box component="span" sx={{ display: 'block', fontSize: 11, letterSpacing: '.04em', fontWeight: 600, color: INK_400 }}>PERIOD</Box>
        <Box component="span" sx={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: GX_INK_700 }}>{timeframe}</Box>
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: INK_400 }}>current vs comparable prior</Box>
      </Box>
    ) : '',
  }
}
export function groupedMetricColumns(pick, keyPrefix = 'gm') {
  const prevTitle = 'The equal-length window immediately before the selected one.'
  const curTitle = 'The selected window (follows the period filter).'
  const pctTitle = '% change: (Current − Comparable) ÷ Comparable.'
  return [
    { key: `${keyPrefix}_prevShip`, header: 'Comparable', align: 'right', group: 'ship', edge: 'left', title: prevTitle, sortValue: (r) => pick(r).prevShipments, cell: (r) => integer(pick(r).prevShipments) },
    { key: `${keyPrefix}_ship`, header: 'Current', align: 'right', group: 'ship', title: curTitle, sortValue: (r) => pick(r).shipments, cell: (r) => integer(pick(r).shipments) },
    { key: `${keyPrefix}_shipPct`, header: 'Δ %', align: 'right', group: 'ship', edge: 'right', title: pctTitle, sortValue: (r) => pick(r).shipmentChangePct, cell: (r) => <GrowthPct pct={pick(r).shipmentChangePct} /> },
    { key: `${keyPrefix}_prevRev`, header: 'Comparable', align: 'right', group: 'rev', edge: 'left', title: prevTitle, sortValue: (r) => pick(r).prevRevenue, cell: (r) => currency(pick(r).prevRevenue) },
    { key: `${keyPrefix}_rev`, header: 'Current', align: 'right', group: 'rev', title: curTitle, sortValue: (r) => pick(r).revenue, cell: (r) => currency(pick(r).revenue) },
    { key: `${keyPrefix}_revPct`, header: 'Δ %', align: 'right', group: 'rev', edge: 'right', title: pctTitle, sortValue: (r) => pick(r).revenueChangePct, cell: (r) => <GrowthPct pct={pick(r).revenueChangePct} /> },
  ]
}

// Reference Panel: collapsible section card, title + (count) + chevron, CLOSED by default.
const PANEL_VARIANTS = {
  default: { mb: 3, iconSx: { fontSize: 16 }, muted: GX_INK_500, chevronSx: { color: GX_INK_500 }, buttonSx: { background: 'none' } },
  hubspot: { mb: 2, countInTitle: true, muted: INK_400, chevronSx: { fontSize: 18, color: INK_400 }, buttonSx: { bgcolor: 'transparent', fontFamily: 'inherit' } },
}
export function GxCollapsePanel({ title, subtitle, icon, count, defaultOpen = false, variant = 'default', children }) {
  const [open, setOpen] = useState(defaultOpen)
  const p = PANEL_VARIANTS[variant] || PANEL_VARIANTS.default
  const titleSx = { fontSize: 14, fontWeight: 600, color: GX_INK_700 }
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, mb: p.mb, overflow: 'hidden' }}>
      <Box
        component="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left',
          border: 'none', cursor: 'pointer', px: 2.5, py: 2, ...p.buttonSx,
        }}
      >
        {icon && <Box sx={{ color: GX_COLORS.indigo, display: 'flex', ...(p.iconSx || {}) }}>{icon}</Box>}
        <Box sx={{ flex: 1 }}>
          {p.countInTitle ? (
            <Typography sx={titleSx}>
              {title}{count != null && <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: p.muted }}>({integer(count)})</Box>}
            </Typography>
          ) : (
            <>
              <Typography component="span" sx={titleSx}>{title}</Typography>
              {count != null && <Typography component="span" sx={{ fontSize: 12, color: p.muted, ml: 1 }}>({integer(count)})</Typography>}
            </>
          )}
          {subtitle && <Typography sx={{ fontSize: 12, color: p.muted }}>{subtitle}</Typography>}
        </Box>
        {open ? <ExpandLessIcon sx={p.chevronSx} /> : <ExpandMoreIcon sx={p.chevronSx} />}
      </Box>
      {open && <Box sx={{ borderTop: LINE }}>{children}</Box>}
    </Paper>
  )
}
